<template>
  
  <div class="post-detail-container" >
    <div class="private-alert-container" v-if="showPrivateAlert">
      <el-card class="private-alert-card" shadow="hover">
        <div class="private-alert">
          <i class="el-icon-lock"></i>
          <span>Sorry. This post is private and not visible to visitors.</span>
          <el-button type="primary" @click="$router.push('/')" plain size="small">Back to Home</el-button>
        </div>
      </el-card>
    </div>
    <!-- Main Content -->
    <div class="main-content" v-if="!showPrivateAlert">
      <!-- Content Area -->
      <div class="content-area">
        <div class="left-side-panel">
          <div class="post-header" v-if="post">
            <div v-if="post.user_id === $store.state.user_id" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; margin-left: -10px; margin-right: -10px;">
              <div style="margin-left: 10px; display: flex; align-items: center; gap: 8px;">
                <i class="el-icon-refresh" style="font-size: 16px; color: #409EFF;"></i>
                <span style="font-size: 14px;">{{ post.view_info?.view_count || 0 }} Reviews</span>
              </div>
              <div class="edit-button-container" style="margin-right: 10px;">
                <el-button
                  v-if="!isCreatorMode"
                  type="warning"
                  size="small"
                  icon="el-icon-view"
                  @click="enterCreatorMode"
                >
                  Enter Creator Mode
                </el-button>
                <template v-else>
                  <el-button
                    type="info"
                    size="small"
                    icon="el-icon-view"
                    @click="exitCreatorMode"
                  >
                    Visitor Mode
                  </el-button>
                  <el-button
                    type="primary"
                    size="small"
                    icon="el-icon-edit"
                    @click="editPost"
                    style="margin-left: 8px;"
                  >
                    Edit Stack
                  </el-button>
                </template>
              </div>
            </div>

            <h2 class="post-title">{{ post.title }}</h2>

            <div style="margin-top: 12px; margin-bottom: 12px;">
              <span style="font-size: 12px; color: #8c8c8c; font-weight: normal; line-height: 1.8;">
                <span>By</span>
                <el-avatar :size="16" :src="post.authorAvatar" style="margin: 0 6px; vertical-align: middle; position: relative; top: -2px;">
                  {{ post.user_name?.charAt(0) }}
                </el-avatar>
                <router-link
                  :to="{ path: '/profile', query: { id: post.user_id }}"
                  class="user-name-link"
                >
                  {{ post.user_name }}{{ post.user_id === $store.state.user_id ? ' (You)' : '' }}
                </router-link><template v-if="post.user_id === $store.state.user_id && post.status === 'PACKAGED'"> • From <i class="el-icon-collection"></i> {{ post.collection_title || 'Unknown Course' }}</template> • <i class="el-icon-time"></i> Updated on {{ formatDate(post.update_time ? post.update_time * 1000 : post.create_time * 1000) }}
              </span>
            </div>

            <!-- Post Interaction Buttons -->
            <div class="post-interactions">
              <div style="display: flex; gap: 6px; align-items: center;">
                <el-tag
                  v-if="post.user_id === $store.state.user_id && post.status === 'PRIVATE'"
                  type="info"
                  size="small"
                  effect="dark"
                >
                  PRIVATE
                </el-tag>
                <el-tag type="primary" size="small">
                  <i class="el-icon-document"></i>
                  {{ post.answers?.length || 0 }}
                </el-tag>
              </div>
              <div style="margin-left: auto; display: flex; gap: 0;">
                <el-button
                  type="text"
                  @click="handleThumbUp"
                  :loading="thumbUpLoading"
                  :class="{ 'is-thumbed-up': isThumbedUp }"
                >
                  <i class="el-icon-thumb"></i>
                  {{ post.thumb_up_count || 0 }}
                </el-button>
                <el-button
                  type="text"
                  @click="handleMark"
                  :loading="markLoading"
                  :class="{ 'is-marked': isMarked }"
                >
                  <i :class="isMarked ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                  {{ post.mark_count || 0 }}
                </el-button>
                <el-button
                  type="text"
                  @click="sharePost"
                >
                  <i class="el-icon-share"></i>
                  {{ post.share_count || 0 }}
                </el-button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Current Post Card -->
        <div class="post-card-container">
          <div class="content-wrapper">
            <el-card class="post-card" v-if="post.answers && post.answers.length > 0">
              <div class="post-content">
                <div class="answer-display">
                  <el-card class="answer-card" shadow="hover">
                      <div class="answer-content clickable-answer-content" @click="handleAnswerCardClick" style="cursor: pointer;">
                        <template v-if="post.answers[currentAnswerIndex].content_type === 'ending'">
                          <div class="text-content" v-html="post.answers[currentAnswerIndex].content"></div>
                        </template>
                        <template v-else-if="post.answers[currentAnswerIndex].content_type === 'text'">
                          <div class="text-content">
                            <div v-html="getFormattedContent(post.answers[currentAnswerIndex].content)"></div>
                          </div>
                        </template>
                        <template v-else-if="post.answers[currentAnswerIndex].content_type === 'image'">
                          <div class="image-content">
                            <img v-if="post.answers[currentAnswerIndex].fileList && post.answers[currentAnswerIndex].fileList.length > 0"
                              :src="post.answers[currentAnswerIndex].fileList[0].url"
                              :alt="'Content ' + (currentAnswerIndex + 1)"
                            >
                          </div>
                        </template>
                        <template v-else-if="post.answers[currentAnswerIndex].content_type === 'video'">
                          <div class="video-content">
                            <video v-if="post.answers[currentAnswerIndex].fileList && post.answers[currentAnswerIndex].fileList.length > 0" controls>
                              <source :src="post.answers[currentAnswerIndex].fileList[0].url" :type="post.answers[currentAnswerIndex].fileList[0].mime_type">
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </template>
                        <template v-else-if="post.answers[currentAnswerIndex].content_type === 'quiz'">
                          <div class="quiz-content">
                            <div class="quiz-question"><strong>Question:</strong> <span>{{ post.answers[currentAnswerIndex].quiz_content.question }}</span></div>
                            <div
                              v-for="(option, idx) in post.answers[currentAnswerIndex].quiz_content.options.filter(option => option !== '')"
                              :key="idx"
                              class="quiz-option"
                              :class="{
                                'correct': (post.view_info?.quiz_answers?.[post.answers[currentAnswerIndex].uniqueCode] != undefined || isCreatorMode) &&
                                  ['A','B','C','D'][idx] === post.answers[currentAnswerIndex].quiz_content.correct_answer,
                                'incorrect': post.view_info?.quiz_answers?.[post.answers[currentAnswerIndex].uniqueCode] != undefined &&
                                  post.view_info?.quiz_answers?.[post.answers[currentAnswerIndex].uniqueCode]?.['answer'] !== post.answers[currentAnswerIndex].quiz_content.correct_answer &&
                                  !isCreatorMode &&
                                  ['A','B','C','D'][idx] === post.view_info?.quiz_answers?.[post.answers[currentAnswerIndex].uniqueCode]?.['answer']
                              }"
                            >
                              <div class="quiz-option-content">
                                <div style="display: flex; flex: 1; align-items: flex-start;">
                                  <span style="min-width: 30px; line-height: 1.5;">{{ ['A','B','C','D'][idx] }}.</span>
                                  <span style="line-height: 1.5;">{{ option }}</span>
                                </div>
                                <span
                                  v-if="(post.view_info?.quiz_answers?.[post.answers[currentAnswerIndex].uniqueCode] != undefined || isCreatorMode) &&
                                    ['A','B','C','D'][idx] === post.answers[currentAnswerIndex].quiz_content.correct_answer"
                                  class="el-icon-check correct-marker"
                                >
                                </span>
                                <span
                                  v-if="post.view_info?.quiz_answers?.[post.answers[currentAnswerIndex].uniqueCode] != undefined &&
                                    ['A','B','C','D'][idx] === post.view_info?.quiz_answers?.[post.answers[currentAnswerIndex].uniqueCode]?.['answer'] &&
                                    post.view_info?.quiz_answers?.[post.answers[currentAnswerIndex].uniqueCode]?.['answer'] !== post.answers[currentAnswerIndex].quiz_content.correct_answer &&
                                    !isCreatorMode"
                                  class="el-icon-close incorrect-marker"
                                >
                                </span>
                              </div>
                            </div>
                            <div v-if="post.view_status === 'YES' || isCreatorMode" class="quiz-explanation">
                              <div class="explanation-header">Explanation:</div>
                              <div class="explanation-content">{{ post.answers[currentAnswerIndex].quiz_content.explanation }}</div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </el-card>
                  <div class="card-footer">
                    <div class="answer-actions">
                      <el-button
                        type="text"
                        class="note-button"
                        @click.stop="addNote"
                      >
                        <i class="el-icon-edit-outline"></i>
                      </el-button>
                      <el-button
                        type="text"
                        class="star-button"
                        @click.stop="toggleStar"
                        :class="{ 'is-starred': currentAnswer?.is_starred }"
                      >
                        <i class="el-icon-collection-tag"></i>
                      </el-button>
                    </div>
                  </div>
                </div>
              </div>
            </el-card>

            <!-- Answer Controls - Horizontal Paginator Below Card -->
            <div class="answer-controls" v-if="post.answers && post.answers.length > 1">
              <span class="answer-number">
                #{{ currentAnswerIndex + 1 }} of {{ post.answers.length }}
              </span>
              <div class="navigation-row">
                <el-button
                  v-if="currentAnswerIndex > 0"
                  class="nav-button prev"
                  icon="el-icon-arrow-left"
                  circle
                  @click="previousAnswer"
                ></el-button>
                <div v-else class="nav-button-placeholder"></div>

                <div class="answer-navigation">
                  <span
                    v-for="(answer, index) in post.answers"
                    :key="index"
                    :class="['nav-dot', { active: index === currentAnswerIndex }, { 'ending-dot': answer.is_ending_card }]"
                    @click="goToAnswer(index)"
                  ></span>
                </div>

                <el-button
                  v-if="currentAnswerIndex < post.answers.length - 1"
                  class="nav-button next"
                  icon="el-icon-arrow-right"
                  circle
                  @click="nextAnswer"
                ></el-button>
                <div v-else class="nav-button-placeholder"></div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="right-side-panel" :class="{ 'collapsed': isCommentsPanelCollapsed }">
          <el-button
            class="toggle-comments-btn"
            :icon="isCommentsPanelCollapsed ? 'el-icon-d-arrow-left' : 'el-icon-d-arrow-right'"
            size="small"
            circle
            @click="toggleCommentsPanel"
            :title="isCommentsPanelCollapsed ? 'Show Comments' : 'Hide Comments'"
          ></el-button>
          <el-card class="stats-card" v-show="!isCommentsPanelCollapsed">
            <div class="stats-content">
              <h3><i class="el-icon-chat-dot-round"></i> Comments ({{ totalComments }})</h3>
              
              <div class="comments-list">
                <div v-if="commentsLoading" class="loading-state">
                  <i class="el-icon-loading"></i>
                  <span>Loading comments...</span>
                </div>
                
                <div v-else-if="comments.length === 0" class="empty-comments">
                  <i class="el-icon-chat-line-round"></i>
                  <p>No comments yet</p>
                  <span>Be the first to comment!</span>
                </div>
                
                <div v-else class="comment-items">
                  <div 
                    v-for="comment in comments" 
                    :key="comment.comment_id"
                    class="comment-item"
                  >
                    <div class="comment-header">
                      <el-avatar :size="28" :src="comment.user.avatar_url">
                        {{ comment.user.user_name?.charAt(0) }}
                      </el-avatar>
                      <div class="comment-user-info">
                        <router-link 
                          :to="{ path: '/profile', query: { id: comment.user.user_id }}"
                          class="comment-username"
                        >
                          {{ comment.user.user_name }}
                        </router-link>
                        <span class="comment-time">{{ formatCommentTime(comment.created_at) }}</span>
                      </div>
                      <div class="comment-options" v-if="comment.user.user_id === $store.state.user_id">
                        <el-dropdown trigger="click">
                          <el-button type="text" size="mini" icon="el-icon-more"></el-button>
                          <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item @click="deleteComment(comment.comment_id)">
                              <i class="el-icon-delete"></i> Delete
                            </el-dropdown-item>
                          </el-dropdown-menu>
                        </el-dropdown>
                      </div>
                    </div>
                    
                    <div class="comment-body">
                      <p>{{ comment.content }}</p>
                    </div>
                    
                    <div class="comment-footer">
                      <el-button 
                        type="text" 
                        size="mini"
                        @click="likeComment(comment)"
                        :class="{ 'is-liked': comment.is_liked === 'YES' }"
                      >
                        <i :class="comment.is_liked === 'YES' ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                        {{ comment.likes_count || 0 }}
                      </el-button>
                      <el-button 
                        type="text" 
                        size="mini"
                        @click="replyToComment(comment)"
                      >
                        <i class="el-icon-chat-dot-round"></i>
                        Reply
                      </el-button>
                    </div>
                    
                    <div v-if="replyingTo === comment.comment_id" class="reply-input">
                      <el-input
                        v-model="replyContent"
                        type="textarea"
                        :autosize="{ minRows: 2, maxRows: 3 }"
                        :placeholder="`Reply to ${comment.user.user_name}...`"
                        maxlength="300"
                        show-word-limit
                      ></el-input>
                      <div class="reply-actions">
                        <el-button size="mini" @click="cancelReply">Cancel</el-button>
                        <el-button 
                          type="primary" 
                          size="mini"
                          @click="submitReply(comment.comment_id)"
                          :disabled="!replyContent.trim()"
                        >
                          Reply
                        </el-button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-if="hasMoreComments" class="load-more">
                  <el-button 
                    type="text"
                    @click="loadMoreComments"
                    :loading="loadingMore"
                  >
                    Load more comments
                  </el-button>
                </div>
              </div>
              
              <div class="comment-input-section">
                <el-input
                  v-model="newComment"
                  type="textarea"
                  :autosize="{ minRows: 2, maxRows: 4 }"
                  placeholder="Write a comment..."
                  maxlength="500"
                  show-word-limit
                  class="comment-input"
                ></el-input>
                <div class="comment-actions">
                  <el-button 
                    type="primary" 
                    size="small"
                    @click="submitComment"
                    :disabled="!newComment.trim()"
                    :loading="commentLoading"
                  >
                    Post Comment
                  </el-button>
                </div>
              </div>
            </div>
          </el-card>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.user-name-link {
  color: inherit;
  text-decoration: none;
  font-weight: normal;
}

.user-name-link:hover {
  color: inherit;
  text-decoration: none;
}

.post-detail-container {
  height: 100vh;
  display: flex;
  overflow: hidden;
  background-color: #fafaf8;
  min-height: 100vh;
  max-height: 100vh;
}

.private-alert-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fafafa;
  z-index: 10;
}

.private-alert-card {
  width: 100%;
  max-width: 500px;
}

.private-alert {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
}

.private-alert i {
  font-size: 48px;
  color: #F56C6C;
  margin-bottom: 16px;
}

.private-alert span {
  font-size: 16px;
  color: #606266;
  margin-bottom: 24px;
  line-height: 1.5;
}

.private-alert .el-button {
  min-width: 120px;
}

.main-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.content-area {
  flex: 1;
  display: flex;
  padding: 10px 20px;
  gap: 20px;
  overflow: hidden;
  height: calc(100vh - 60px);
  max-height: calc(100vh - 60px);
}

.left-side-panel, .post-card-container, .right-side-panel {
  flex: 1;
  width: 26.3%;
}

.post-card-container {
  flex: 1.8;
  width: 47.4%;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  max-width: 900px;
  margin: 0 auto;
  gap: 20px;
}

.post-card {
  flex: 1;
  width: auto;
  max-height: 100%;
  aspect-ratio: 0.714;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

/* Add these styles to override Element UI's default card padding */
:deep(.post-card.el-card) {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.post-card .el-card__body) {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

.post-header {
  margin: 10px 10px;
  min-height: 60px;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
}

.user-row {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.user-info {
  flex: 1;
}

.post-title {
  margin: 0;
  color: #303133;
  font-size: 20px;
}

.post-interactions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.post-interactions .el-button {
  color: #606266;
}

.post-interactions .el-button.is-thumbed-up,
.post-interactions .el-button.is-thumbed-down,
.post-interactions .el-button.is-marked {
  color: #1159AF;
}

.answer-display {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 0px;
}

.answer-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 15px 0;
  z-index: 10;
  flex-shrink: 0;
  flex-grow: 0;
  width: 100%;
}

.answer-controls > .answer-number {
  order: -1;
  margin: 0;
  margin-bottom: 8px;
}

.answer-navigation {
  display: flex;
  flex-direction: row;
  gap: 12px;
}

.nav-button {
  position: relative;
  z-index: 2;
}

.nav-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #dcdfe6;
  cursor: pointer;
  transition: background-color 0.3s;
}

.nav-dot.active {
  background-color: #409EFF;
}

.nav-dot:hover {
  background-color: #b3d8ff;
}

.nav-dot.ending-dot {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.nav-dot.ending-dot.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transform: scale(1.2);
}

.post-content {
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0px;
}

.answer-card {
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

:deep(.answer-card.el-card) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.answer-card .el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0;
  overflow: hidden;
}

.answer-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 32px;
  overflow-y: auto;
  padding: 20px;
}

.text-content, .image-content, .video-content, .quiz-content {
  min-height: 100%;
}

.quiz-content {
  pointer-events: auto;
  position: relative;
  z-index: 1;
  padding: 20px;
}

.card-footer {
  position: absolute;
  bottom: 0px;
  left: 0;
  right: 0;
  height: 20px;
  border-top: 1px solid #EBEEF5;
  padding: 6px;
  display: flex;
  justify-content: flex-end;
  z-index: 1;
}

.answer-number {
  color: #909399;
  font-size: 14px;
}

.navigation-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
}

.nav-button-placeholder {
  width: 32px;
  height: 32px;
}

.quiz-option {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 12px 20px;
  margin-bottom: 12px;
  margin-top: 12px;
  transition: all 0.3s ease;
  background-color: #FFFFFF;
  pointer-events: auto;
}

.quiz-option.correct {
  background-color: #F0F9EB;
  border-color: #67C23A;
}

.quiz-option.incorrect {
  background-color: #FEF0F0;
  border-color: #F56C6C;
}

.quiz-option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: clamp(6px, 1.2vw, 24px);
}

.correct-marker {
  margin-left: 10px;
  color: #67c23a;
  padding: 10px;
  font-size: 16px;
}

.incorrect-marker {
  margin-left: 10px;
  color: #F56C6C;
  padding: 10px;
  font-size: 16px;
}

.image-content img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.nav-button-placeholder {
  width: 40px;
  height: 40px;
  position: relative;
  z-index: 2;
}

@media screen and (min-width: 1440px) {
  .post-title {
    font-size: 24px;
  }
  
  .text-content, .image-content, .video-content, .quiz-content {
    font-size: 20px;
  }
}

:deep(.el-card) {
  border: none;
}

.right-side-panel {
  flex: 1;
  width: 26.3%;
  overflow: hidden;
  padding: 0;
  margin-left: 20px;
  position: relative;
}

.toggle-comments-btn {
  position: absolute;
  top: 10px;
  left: -15px;
  z-index: 10;
  background: white;
  border: 1px solid #dcdfe6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);

  &:hover {
    background: #f5f7fa;
  }
}

.quiz-explanation {
  margin-top: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid #409eff;
}

.explanation-header {
  font-weight: bold;
  color: #409eff;
  margin-bottom: 8px;
}

.explanation-content {
  font-size: clamp(6px, 1.2vw, 24px);
  line-height: 1.5;
  color: #606266;
}

.text-content {
  font-size: clamp(8px, 1.5vw, 32px);
  line-height: 1.5;
  color: #303133;
}

.text-content :deep(h1),
.text-content :deep(h2),
.text-content :deep(h3),
.text-content :deep(h4),
.text-content :deep(h5),
.text-content :deep(h6) {
  margin-top: 0.8em;
  margin-bottom: 0.5em;
  color: #303133;
  font-weight: 600;
}

.text-content :deep(h1) { font-size: 1.8em; }
.text-content :deep(h2) { font-size: 1.5em; }
.text-content :deep(h3) { font-size: 1.3em; }
.text-content :deep(h4) { font-size: 1.1em; }
.text-content :deep(h5) { font-size: 1em; }
.text-content :deep(h6) { font-size: 0.9em; }

.text-content :deep(p) {
  margin: 0.5em 0;
  line-height: 1.6;
}

.text-content :deep(code) {
  background-color: #f5f5f5;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.9em;
  color: #e83e8c;
}

.text-content :deep(pre) {
  background-color: #f8f9fa;
  padding: 1em;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1em 0;
  border: 1px solid #e9ecef;
}

.text-content :deep(pre code) {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

.text-content :deep(blockquote) {
  border-left: 4px solid #409eff;
  padding-left: 1em;
  margin: 1em 0;
  color: #606266;
  background-color: #f5f7fa;
  padding: 0.5em 1em;
  border-radius: 0 4px 4px 0;
}

.text-content :deep(ul),
.text-content :deep(ol) {
  padding-left: 2em;
  margin: 0.5em 0;
}

.text-content :deep(li) {
  margin: 0.3em 0;
  line-height: 1.6;
}

.text-content :deep(a) {
  color: #409eff;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.3s;
}

.text-content :deep(a:hover) {
  border-bottom-color: #409eff;
}

.text-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
}

.text-content :deep(th),
.text-content :deep(td) {
  border: 1px solid #dcdfe6;
  padding: 8px 12px;
  text-align: left;
}

.text-content :deep(th) {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #303133;
}

.text-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 0.5em 0;
}

.text-content :deep(hr) {
  border: none;
  border-top: 1px solid #e4e7ed;
  margin: 1.5em 0;
}

.text-content :deep(strong) {
  font-weight: 600;
  color: #303133;
}

.text-content :deep(em) {
  font-style: italic;
}

.text-content :deep(del) {
  text-decoration: line-through;
  color: #909399;
}

.quiz-question {
  font-size: clamp(8px, 1.5vw, 32px);
  line-height: 1.5;
}

.answer-actions {
  display: flex;
  gap: 10px;
  margin-right: 10px;
  align-items: center;
}

.note-button, .star-button, .share-button {
  margin-top: 0px;
  height: 20px;
  padding: 0px 8px;
  font-size: 24px;
  color: #909399;
  transition: all 0.3s ease;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 5px;
}

.note-button:hover, .star-button:hover, .share-button:hover {
  transform: scale(1.1);
}

.count-badge {
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  background-color: #f2f6fc;
  border-radius: 10px;
  padding: 2px 6px;
  min-width: 18px;
  text-align: center;
  line-height: 1.2;
  display: inline-block;
  margin-left: 2px;
}

.note-button:hover .count-badge, 
.star-button:hover .count-badge,
.share-button:hover .count-badge {
  background-color: #ecf5ff;
  color: #409EFF;
}

.star-button i {
  transition: all 0.3s ease;
}

.share-button i {
  transition: all 0.3s ease;
}

/* Additional responsive styles for count badges */
@media screen and (min-width: 1440px) {
  .count-badge {
    font-size: 16px;
    padding: 3px 8px;
  }
}

/* Add this style for when the button is active/pressed */
.note-button:active .count-badge, 
.star-button:active .count-badge,
.share-button:active .count-badge {
  transform: scale(0.95);
}

.stats-card {
  height: 100%;
  overflow-y: auto;
}

.stats-content {
  padding: 20px;
}

.stats-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #303133;
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  color: #606266;
}

.stat-row i {
  font-size: 18px;
  color: #409EFF;
}

.edit-button-container {
  margin-left: auto;
}

.cryptic-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  height: 100%;
  min-height: 300px;
  background: white;
  border-radius: 8px;
  border: 1px dashed #dcdfe6;
}

.lock-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #f5f7fa;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.lock-icon i {
  font-size: 40px;
  color: #909399;
}

.cryptic-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 20px 0;
}

.access-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px;
  width: 100%;
  max-width: 300px;
}

.access-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 20px;
  font-size: 14px;
  margin-bottom: 10px;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.access-badge i {
  margin-right: 8px;
  font-size: 18px;
}

.access-badge.exchange {
  background-color: #ecf5ff;
  color: #409EFF;
  border: 1px solid #d9ecff;
}

.access-badge.payment {
  background-color: #fdf6ec;
  color: #E6A23C;
  border: 1px solid #faecd8;
}

.access-button {
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.access-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.subscriber-button {
  background-color: #409EFF;
}

.payment-button {
  background-color: #E6A23C;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .cryptic-placeholder {
    padding: 30px 15px;
    min-height: 250px;
  }
  
  .lock-icon {
    width: 60px;
    height: 60px;
  }
  
  .lock-icon i {
    font-size: 30px;
  }
  
  .cryptic-title {
    font-size: 20px;
  }
  
  .access-badge {
    font-size: 12px;
  }
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
}

.preview-button {
  background-color: #909399;
  border-color: #909399;
}

/* Additional responsive styles for the buttons */
@media (max-width: 480px) {
  .action-buttons {
    flex-direction: column;
    width: 100%;
  }
  
  .access-button {
    width: 100%;
  }
}

/* Comment Section Styles */
.comment-input-section {
  margin: 20px 0 0 0;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.comment-input {
  width: 100%;
  margin-bottom: 12px;
}

.comment-actions {
  display: flex;
  justify-content: flex-end;
}

.comments-list {
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  padding: 10px 0;
}

.loading-state, .empty-comments {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #909399;
  text-align: center;
}

.loading-state i, .empty-comments i {
  font-size: 32px;
  margin-bottom: 12px;
}

.empty-comments p {
  margin: 8px 0;
  font-size: 14px;
  font-weight: 500;
}

.empty-comments span {
  font-size: 12px;
  color: #c0c4cc;
}

.comment-item {
  padding: 16px 0;
  border-bottom: 1px solid #f2f6fc;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-item .comment-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.comment-user-info {
  flex: 1;
  margin-left: 10px;
  display: flex;
  flex-direction: column;
}

.comment-username {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  text-decoration: none;
  line-height: 1.2;
}

.comment-username:hover {
  color: #409eff;
}

.comment-time {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.comment-options {
  margin-left: auto;
}

.comment-body {
  margin-left: 38px;
  margin-bottom: 8px;
}

.comment-body p {
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
  margin: 0;
  word-wrap: break-word;
}

.comment-footer {
  margin-left: 38px;
  display: flex;
  gap: 16px;
}

.comment-footer .el-button {
  padding: 0;
  font-size: 12px;
  color: #909399;
}

.comment-footer .el-button:hover {
  color: #409eff;
}

.comment-footer .el-button.is-liked {
  color: #f7ba2a;
}

.reply-input {
  margin: 12px 0 0 38px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.reply-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.load-more {
  padding: 20px;
  text-align: center;
}

.load-more .el-button {
  font-size: 14px;
  color: #409eff;
}
</style>

<script>
import { postRequest } from '@/apis/front'
import { renderFormattedContent } from '@/utils/contentFormatter'

export default {
  name: 'PostDetail',
  data() {
    return {
      postId: null,
      postUserId: null,
      post: {
        id: null,
        title: '',
        content: '',
        authorName: '',
        authorAvatar: '',
        createdAt: '',
        likes: 0,
        comments: []
      },
      showAnswer: false,
      newComment: '',
      isLiked: false,
      currentAnswerIndex: 0,
      showPrivateAlert: false,
      comments: [],
      totalComments: 0,
      currentCommentPage: 1,
      commentLimit: 20,
      hasMoreComments: false,
      commentsLoading: false,
      commentLoading: false,
      loadingMore: false,
      replyingTo: null,
      replyContent: '',
      thumbUpLoading: false,
      thumbDownLoading: false,
      markLoading: false,
      isThumbedUp: false,
      isThumbedDown: false,
      isMarked: false,
      isCreatorMode: false,
      isCommentsPanelCollapsed: false
    }
  },
  computed: {
    isAuthor() {
      // Compare current user ID with post author ID
    //   const currentUser = this.$store.state.user // Assuming you store user in Vuex
       return this.$store.state.user_id === this.postUserId

    },
    currentAnswer() {
      if (!this.post || !this.post.answers || this.post.answers.length === 0) return null
      return this.post.answers[this.currentAnswerIndex]
    }
  },
  created() {
    this.postId = this.$route.query.id
    this.postUserId = this.$route.query.user_id

    // Set initial card index if provided in URL
    if (this.$route.query.cardIndex !== undefined) {
      this.currentAnswerIndex = parseInt(this.$route.query.cardIndex) || 0
    }

    console.log(this.$store.state.user_id, this.postUserId)
    this.fetchPostDetail(this.postId)
    this.fetchComments()
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    enterCreatorMode() {
      this.isCreatorMode = true
      this.$message.success('Entered Creator Mode - All answers and explanations are now visible')
    },
    exitCreatorMode() {
      this.isCreatorMode = false
      this.$message.info('Exited Creator Mode - Returned to Visitor Mode')
    },
    toggleCommentsPanel() {
      this.isCommentsPanelCollapsed = !this.isCommentsPanelCollapsed
    },
    getFormattedContent(content) {
      if (!content) return ''
      const result = renderFormattedContent(content)
      return result.html
    },
    async fetchPostDetail(postId) {
      try {
        const response = await postRequest('getPost', { 
          post_id: postId,
          user_id: this.$store.state.user_id 
        })
        if (response.data.code === 0) {
          this.post = response.data.data
          this.showAnswer = this.isAuthor || this.post.view_status === 'YES'

          // Add ending card to answers array
          if (this.post.answers && this.post.answers.length > 0) {
            await this.addEndingCard()
          }

          // Initialize interaction states from view_info
          if (this.post.view_info) {
            this.isThumbedUp = this.post.view_info.is_thumbed_up === 'YES'
            this.isThumbedDown = this.post.view_info.is_thumbed_down === 'YES'
            this.isMarked = this.post.view_info.is_marked === 'YES'
          }

          // Call updatePostView after successful post fetch
          if(this.showAnswer) {
            await this.updatePostView()
          }

          // Set up ending card button listener
          this.$nextTick(() => {
            this.setupEndingCardButton()
          })
        }else if(response.data.code === -2){
          this.showPrivateAlert = true
        } else {
          throw new Error(response.data.msg || 'Failed to load post')
        }
      } catch (error) {
        this.$message.error('Failed to load post details')
        console.error('Error fetching post details:', error)
      }
    },
    //Need to get view_info first
    async updatePostView() {
      try {
        const response = await postRequest('updatePostView', {
          user_id: this.$store.state.user_id,
          post_id: this.postId,
          read_answer_index: 0,
          read_scene: 'post_detail',
          view_method:this.post.view_info.view_method,
          start_status: false
        })

        if (response.data.code !== 0) {
          console.error('Failed to update post view:', response.data.msg)
        }
      } catch (error) {
        console.error('Error updating post view:', error)
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString()
    },
    editPost() {
      localStorage.setItem('editPostId', this.postId)
      this.$router.push('/edit-stack')
    },
    async handleLike() {
      try {
        // TODO: Replace with actual API call
        // await toggleLike(this.post.id)
        this.isLiked = !this.isLiked
        this.post.likes += this.isLiked ? 1 : -1
      } catch (error) {
        this.$message.error('Failed to update like')
      }
    },
    focusCommentInput() {
      this.$refs.commentInput.focus()
    },
    async submitComment() {
      if (!this.newComment.trim()) return

      try {
        // TODO: Replace with actual API call
        // await submitComment(this.post.id, this.newComment)
        
        // Mock adding comment
        this.post.comments.unshift({
          id: Date.now(),
          authorName: 'Current User',
          authorAvatar: '',
          content: this.newComment,
          createdAt: new Date().toISOString()
        })
        
        this.newComment = ''
        this.$message.success('Comment added successfully!')
      } catch (error) {
        this.$message.error('Failed to add comment')
      }
    },
    goBack() {
      this.$router.go(-1)
    },
    previousAnswer() {
      if (this.currentAnswerIndex > 0) {
        this.currentAnswerIndex--;
      }
    },
    nextAnswer() {
      if (this.currentAnswerIndex < this.post.answers.length - 1) {
        this.currentAnswerIndex++;
        this.$nextTick(() => {
          if (this.post.answers[this.currentAnswerIndex]?.is_ending_card) {
            this.setupEndingCardButton();
          }
        });
      }
    },
    goToAnswer(index) {
      this.currentAnswerIndex = index;
      this.$nextTick(() => {
        if (this.post.answers[this.currentAnswerIndex]?.is_ending_card) {
          this.setupEndingCardButton();
        }
      });
    },
    handleKeydown(e) {
      if (!this.post || !this.post.answers || this.post.answers.length === 0) return;
      if (e.key === 'ArrowUp') {
        this.previousAnswer();
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        this.nextAnswer();
        e.preventDefault();
      }
    },
    handleAnswerCardClick() {
      if (!this.post || !this.post.answers || this.post.answers.length === 0) return;

      // Don't advance if we're on the ending card
      if (this.post.answers[this.currentAnswerIndex]?.is_ending_card) {
        return;
      }

      if (this.currentAnswerIndex < this.post.answers.length - 1) {
        this.nextAnswer();
      }
    },

    async addEndingCard() {
      // Check if takeaway card already exists
      let existingSummary = null
      if (this.post.view_info && this.post.view_info.takeaway_card_id) {
        // Fetch the existing takeaway card content
        try {
          const cardResponse = await postRequest('getCard', {
            card_id: this.post.view_info.takeaway_card_id,
            user_id: this.$store.state.user_id
          })
          if (cardResponse.data.code === 0 && cardResponse.data.data) {
            existingSummary = cardResponse.data.data.content
          }
        } catch (error) {
          console.error('Error fetching existing takeaway:', error)
        }
      }

      const endingCard = {
        content_type: 'ending',
        content: existingSummary ? this.generateSummaryView(existingSummary) : this.generateButtonView(),
        is_ending_card: true
      }
      this.post.answers.push(endingCard)
    },

    generateSummaryView(summary) {
      return `
        <div style="text-align: center; padding: 40px 20px;">
          <div style="font-size: 60px; margin-bottom: 20px;">🎉</div>
          <h2 style="font-size: 32px; font-weight: 700; margin-bottom: 16px; color: #303133;">Congratulations!</h2>
          <p style="font-size: 18px; line-height: 1.6; margin-bottom: 40px; color: #606266;">You've completed this stack. Here's your takeaway:</p>
          <div style="background: rgba(103, 194, 58, 0.05); border: 1px solid rgba(103, 194, 58, 0.2); border-radius: 8px; padding: 24px; margin-bottom: 24px; text-align: left;">
            <div style="display: flex; align-items: center; margin-bottom: 12px; color: #67c23a;">
              <span style="font-size: 20px; margin-right: 8px;">📝</span>
              <span style="font-weight: 600; font-size: 16px;">Key Takeaways</span>
            </div>
            <div style="color: #606266; font-size: 16px; line-height: 1.8; white-space: pre-wrap;">${summary}</div>
          </div>
        </div>
      `
    },

    generateButtonView() {
      return `
        <div style="text-align: center; padding: 40px 20px;">
          <div style="font-size: 60px; margin-bottom: 20px;">🎉</div>
          <h2 style="font-size: 32px; font-weight: 700; margin-bottom: 16px; color: #303133;">Congratulations!</h2>
          <p style="font-size: 18px; line-height: 1.6; margin-bottom: 40px; color: #606266;">You've completed this stack. Great job on expanding your knowledge!</p>
          <div style="background: rgba(64, 158, 255, 0.1); border-radius: 8px; padding: 24px; margin-bottom: 24px;">
            <p style="font-size: 16px; margin-bottom: 16px; color: #606266;">Want to save the key insights from this stack?</p>
            <button id="generate-takeaway-btn" style="
              background: #409EFF;
              color: white;
              border: none;
              padding: 12px 24px;
              font-size: 16px;
              font-weight: 600;
              border-radius: 4px;
              cursor: pointer;
              transition: all 0.3s ease;
            " onmouseover="this.style.background='#66b1ff'; this.style.transform='translateY(-2px)'" onmouseout="this.style.background='#409EFF'; this.style.transform='translateY(0)'">
              <span style="margin-right: 8px;">📝</span> Generate a Takeaway Knowit
            </button>
          </div>
          <div id="takeaway-message" style="display: none; margin-top: 20px; padding: 12px; border-radius: 4px;"></div>
        </div>
      `
    },

    setupEndingCardButton() {
      const button = document.getElementById('generate-takeaway-btn');
      if (button) {
        button.onclick = async () => {
          const messageDiv = document.getElementById('takeaway-message');
          button.disabled = true;
          button.innerHTML = '<span style="margin-right: 8px;">⏳</span> Generating...';

          try {
            const response = await postRequest('GeneratePrivateCardOnPost', {
              user_id: this.$store.state.user_id,
              post_id: this.postId
            });

            console.log('GeneratePrivateCardOnPost response:', response.data);

            if (response.data.code === 0) {
              messageDiv.style.display = 'block';
              messageDiv.style.background = 'rgba(103, 194, 58, 0.1)';
              messageDiv.style.color = '#67c23a';
              messageDiv.innerHTML = '<span style="margin-right: 8px;">✅</span> Takeaway Knowit created successfully! Check your Stack Takeaways collection.';
              this.$message.success('Takeaway Knowit created and saved to Stack Takeaways');

              // Log the summary for debugging
              if (response.data.data && response.data.data.summary) {
                console.log('Generated summary:', response.data.data.summary);
              }

              // Reload the ending card to show the summary
              const savedIndex = this.currentAnswerIndex
              setTimeout(() => {
                this.fetchPostDetail(this.postId).then(() => {
                  this.currentAnswerIndex = savedIndex
                })
              }, 1500)
            } else {
              throw new Error(response.data.msg || 'Failed to create takeaway');
            }
          } catch (error) {
            console.error('Error generating takeaway:', error);
            messageDiv.style.display = 'block';
            messageDiv.style.background = 'rgba(245, 108, 108, 0.1)';
            messageDiv.style.color = '#f56c6c';
            messageDiv.innerHTML = '<span style="margin-right: 8px;">⚠️</span> An error occurred while generating takeaway';
            this.$message.error('An error occurred while generating takeaway');
            button.disabled = false;
            button.innerHTML = '<span style="margin-right: 8px;">📝</span> Generate a Takeaway Knowit';
          }
        };
      }
    },
    
    handlePaymentAccess() {
      this.$router.push(`/post-order?id=${this.postId}`);
    },
    handlePreviewAccess() {
      // Implement preview logic
      this.$message.info('Loading preview content...');
      // You might want to show a preview version of the content
      // or request a preview from the server
      this.loadPreviewContent();
    },
    loadPreviewContent() {
      // Implement API call to get preview content
      postRequest('getPostPreview', {
        post_id: this.postId,
        user_id: this.$store.state.user_id
      })
      .then(response => {
        if (response.data.code === 0) {
          // Show preview content
          this.$notify({
            title: 'Preview Available',
            message: 'Showing a preview of the content',
            type: 'success'
          });
          // Handle preview content display
          // Maybe set a previewContent data property and show it
        } else {
          this.$message.error(response.data.msg || 'Failed to load preview');
        }
      })
      .catch(error => {
        console.error('Error loading preview:', error);
        this.$message.error('Failed to load preview');
      });
    },
    
    async fetchComments() {
      if (!this.postId) return
      
      this.commentsLoading = true
      try {
        const response = await postRequest('getPostComments', {
          post_id: this.postId,
          user_id: this.$store.state.user_id,
          page: this.currentCommentPage,
          limit: this.commentLimit
        })
        
        if (response.data.code === 0) {
          const data = response.data.data
          this.comments = data.comments || []
          this.totalComments = data.total || 0
          this.hasMoreComments = (this.currentCommentPage * this.commentLimit) < this.totalComments
        } else {
          console.error('Failed to fetch comments:', response.data.msg)
        }
      } catch (error) {
        console.error('Error fetching comments:', error)
        this.$message.error('Failed to load comments')
      } finally {
        this.commentsLoading = false
      }
    },
    
    async loadMoreComments() {
      if (!this.hasMoreComments || this.loadingMore) return
      
      this.loadingMore = true
      try {
        this.currentCommentPage++
        const response = await postRequest('getPostComments', {
          post_id: this.postId,
          user_id: this.$store.state.user_id,
          page: this.currentCommentPage,
          limit: this.commentLimit
        })
        
        if (response.data.code === 0) {
          const data = response.data.data
          this.comments.push(...(data.comments || []))
          this.hasMoreComments = (this.currentCommentPage * this.commentLimit) < data.total
        } else {
          this.currentCommentPage--
          this.$message.error('Failed to load more comments')
        }
      } catch (error) {
        this.currentCommentPage--
        console.error('Error loading more comments:', error)
        this.$message.error('Failed to load more comments')
      } finally {
        this.loadingMore = false
      }
    },
    
    async submitComment() {
      if (!this.newComment.trim()) return
      
      if (!this.$store.state.user_id) {
        this.$message.warning('Please login to comment')
        return
      }
      
      this.commentLoading = true
      try {
        const response = await postRequest('createComment', {
          user_id: this.$store.state.user_id,
          post_id: this.postId,
          content: this.newComment.trim()
        })
        
        if (response.data.code === 0) {
          this.$message.success('Comment posted successfully')
          this.newComment = ''
          this.currentCommentPage = 1
          await this.fetchComments()
        } else {
          this.$message.error(response.data.msg || 'Failed to post comment')
        }
      } catch (error) {
        console.error('Error posting comment:', error)
        this.$message.error('Failed to post comment')
      } finally {
        this.commentLoading = false
      }
    },
    
    async deleteComment(commentId) {
      this.$confirm('Are you sure you want to delete this comment?', 'Confirm Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await postRequest('deleteComment', {
            comment_id: commentId,
            user_id: this.$store.state.user_id
          })
          
          if (response.data.code === 0) {
            this.$message.success('Comment deleted successfully')
            this.comments = this.comments.filter(c => c.comment_id !== commentId)
            this.totalComments = Math.max(0, this.totalComments - 1)
          } else {
            this.$message.error(response.data.msg || 'Failed to delete comment')
          }
        } catch (error) {
          console.error('Error deleting comment:', error)
          this.$message.error('Failed to delete comment')
        }
      }).catch(() => {})
    },
    
    async likeComment(comment) {
      if (!this.$store.state.user_id) {
        this.$message.warning('Please login to like comments')
        return
      }
      
      try {
        const response = await postRequest('likeComment', {
          comment_id: comment.comment_id,
          user_id: this.$store.state.user_id
        })
        
        if (response.data.code === 0) {
          const isLiked = response.data.data.is_liked
          comment.is_liked = isLiked ? 'YES' : 'NO'
          comment.likes_count = Math.max(0, (comment.likes_count || 0) + (isLiked ? 1 : -1))
        } else {
          this.$message.error(response.data.msg || 'Failed to update like')
        }
      } catch (error) {
        console.error('Error liking comment:', error)
        this.$message.error('Failed to update like')
      }
    },
    
    replyToComment(comment) {
      this.replyingTo = comment.comment_id
      this.replyContent = ''
    },
    
    cancelReply() {
      this.replyingTo = null
      this.replyContent = ''
    },
    
    async submitReply(parentCommentId) {
      if (!this.replyContent.trim()) return
      
      if (!this.$store.state.user_id) {
        this.$message.warning('Please login to reply')
        return
      }
      
      try {
        const response = await postRequest('createComment', {
          user_id: this.$store.state.user_id,
          post_id: this.postId,
          content: this.replyContent.trim(),
          parent_comment_id: parentCommentId
        })
        
        if (response.data.code === 0) {
          this.$message.success('Reply posted successfully')
          this.replyContent = ''
          this.replyingTo = null
          this.currentCommentPage = 1
          await this.fetchComments()
        } else {
          this.$message.error(response.data.msg || 'Failed to post reply')
        }
      } catch (error) {
        console.error('Error posting reply:', error)
        this.$message.error('Failed to post reply')
      }
    },
    
    formatCommentTime(timestamp) {
      const date = new Date(timestamp)
      const now = new Date()
      const diff = now - date
      const seconds = Math.floor(diff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)
      
      if (days > 7) {
        return date.toLocaleDateString()
      } else if (days > 0) {
        return `${days} day${days > 1 ? 's' : ''} ago`
      } else if (hours > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`
      } else if (minutes > 0) {
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
      } else {
        return 'Just now'
      }
    },
    
    async sharePost() {
      try {
        // Create the post URL
        const postUrl = `${window.location.origin}/post?id=${this.postId}&user_id=${this.postUserId}`
        
        // Copy the URL to clipboard
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(postUrl)
        } else {
          // Fallback for older browsers or non-HTTPS contexts
          const textArea = document.createElement('textarea')
          textArea.value = postUrl
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          try {
            document.execCommand('copy')
          } catch (err) {
            console.error('Failed to copy using document.execCommand:', err)
            throw new Error('Failed to copy link')
          }
          document.body.removeChild(textArea)
        }
        
        // Record the share activity in backend
        const response = await postRequest('createShare', {
          user_id: this.$store.state.user_id,
          post_id: this.postId,
          shared_url: postUrl,
          share_method: 'copy_link',
          share_platform: 'web'
        })
        
        if (response.data.code === 0) {
          // Update local share count using Vue.set to ensure reactivity
          if (this.post) {
            this.$set(this.post, 'share_count', (this.post.share_count || 0) + 1)
          }
          this.$message.success('Link copied to clipboard!')
        } else {
          // Still show success message for copying, even if backend recording fails
          this.$message.success('Link copied to clipboard!')
          console.error('Failed to record share:', response.data.msg)
        }
      } catch (error) {
        console.error('Error sharing post:', error)
        this.$message.error('Failed to copy link')
      }
    },

    async handleThumbUp() {
      this.thumbUpLoading = true
      try {
        const response = await postRequest('thumbUpPost', {
          user_id: this.$store.state.user_id,
          post_id: this.postId
        })

        if (response.data.code === 0) {
          this.isThumbedUp = !this.isThumbedUp
          if (this.isThumbedUp) {
            this.post.thumb_up_count = (this.post.thumb_up_count || 0) + 1
            if (this.isThumbedDown) {
              this.isThumbedDown = false
              this.post.thumb_down_count = Math.max(0, (this.post.thumb_down_count || 0) - 1)
            }
          } else {
            this.post.thumb_up_count = Math.max(0, (this.post.thumb_up_count || 0) - 1)
          }
        } else {
          this.$message.error(response.data.msg || 'Failed to thumbs up')
        }
      } catch (error) {
        console.error('Error thumbing up:', error)
        this.$message.error('Failed to thumbs up')
      } finally {
        this.thumbUpLoading = false
      }
    },

    async handleThumbDown() {
      this.thumbDownLoading = true
      try {
        const response = await postRequest('thumbDownPost', {
          user_id: this.$store.state.user_id,
          post_id: this.postId
        })

        if (response.data.code === 0) {
          this.isThumbedDown = !this.isThumbedDown
          if (this.isThumbedDown) {
            this.post.thumb_down_count = (this.post.thumb_down_count || 0) + 1
            if (this.isThumbedUp) {
              this.isThumbedUp = false
              this.post.thumb_up_count = Math.max(0, (this.post.thumb_up_count || 0) - 1)
            }
          } else {
            this.post.thumb_down_count = Math.max(0, (this.post.thumb_down_count || 0) - 1)
          }
        } else {
          this.$message.error(response.data.msg || 'Failed to thumbs down')
        }
      } catch (error) {
        console.error('Error thumbing down:', error)
        this.$message.error('Failed to thumbs down')
      } finally {
        this.thumbDownLoading = false
      }
    },

    async handleMark() {
      this.markLoading = true
      try {
        const response = await postRequest('markPost', {
          user_id: this.$store.state.user_id,
          post_id: this.postId
        })

        if (response.data.code === 0) {
          this.isMarked = !this.isMarked
          if (this.isMarked) {
            this.post.mark_count = (this.post.mark_count || 0) + 1
          } else {
            this.post.mark_count = Math.max(0, (this.post.mark_count || 0) - 1)
          }
        } else {
          this.$message.error(response.data.msg || 'Failed to mark')
        }
      } catch (error) {
        console.error('Error marking:', error)
        this.$message.error('Failed to mark')
      } finally {
        this.markLoading = false
      }
    },

    async toggleStar() {
      if (!this.currentAnswer) return

      try {
        const uniqueCode = this.currentAnswer.uniqueCode
        const isCurrentlyStarred = this.currentAnswer.is_starred

        const response = await postRequest('starAnswer', {
          user_id: this.$store.state.user_id,
          post_id: this.postId,
          unique_code: uniqueCode
        })

        if (response.data.code === 0) {
          this.$set(this.currentAnswer, 'is_starred', !isCurrentlyStarred)
          this.$message.success(isCurrentlyStarred ? 'Answer unpinned' : 'Answer pinned')
        } else {
          this.$message.error(response.data.msg || 'Failed to star answer')
        }
      } catch (error) {
        console.error('Error starring answer:', error)
        this.$message.error('Failed to star answer')
      }
    },

    async addNote() {
      // TODO: Implement note functionality
      this.$message.info('Note feature coming soon')
    }
  }
}
</script> 